import React from 'react';
import {
  Bot,
  Brain,
  Database,
  GitBranch,
  Layers,
  Shield,
  ShieldCheck,
  Sparkles,
  User,
  Wrench,
  Zap,
} from 'lucide-react';

export type FlowStepStatus = 'pending' | 'active' | 'completed' | 'skipped';

export interface OrchestrationFlowStep {
  id: string;
  phase: 'ingress' | 'governance' | 'routing' | 'execution' | 'output';
  title: string;
  subtitle: string;
  icon: React.ElementType;
  duration: string;
  status: FlowStepStatus;
  detail: string;
  metadata?: Record<string, string>;
  policyDecision?: string;
  tokens?: number;
  cost?: string;
  model?: string;
  isLlm?: boolean;
}

export const ALL_FLOW_STEPS: OrchestrationFlowStep[] = [
  {
    id: 'prompt',
    phase: 'ingress',
    title: 'User Prompt Received',
    subtitle: 'API Gateway · Finance Copilot',
    icon: User,
    duration: '4ms',
    status: 'pending',
    detail: 'Inbound prompt accepted from authenticated session. Request routed to Agent Orchestration layer.',
    metadata: { channel: 'Chat UI', correlation_id: 'corr-8f2a91c4', payload_size: '1.2 KB' },
  },
  {
    id: 'auth',
    phase: 'ingress',
    title: 'Auth & Tenant Context',
    subtitle: 'Resource Access · Acme Corp',
    icon: ShieldCheck,
    duration: '38ms',
    status: 'pending',
    detail: 'JWT validated, tenant scoped, department=Finance, role=Analyst. MCP credentials resolved.',
    metadata: { tenant: 'Acme Corp', department: 'Finance', user: 'sarah.chen@acme.com' },
  },
  {
    id: 'firewall',
    phase: 'governance',
    title: 'Prompt Firewall Scan',
    subtitle: 'AI Governance · Threat detection',
    icon: Shield,
    duration: '86ms',
    status: 'pending',
    detail: 'Scanned for injection, jailbreak, credential extraction. No active threats. Domain classified as Financial.',
    policyDecision: 'Allow',
    metadata: { classification: 'Financial Analysis', risk: 'Low', confidence: '94.2%' },
  },
  {
    id: 'policy',
    phase: 'governance',
    title: 'Policy Validation',
    subtitle: 'Financial Strict v2.4.1',
    icon: ShieldCheck,
    duration: '42ms',
    status: 'pending',
    detail: 'Enterprise policy chain evaluated: prompt allowed, external model permitted, PII masking required on output.',
    policyDecision: 'Allow · Output mask PII',
    metadata: { source: 'Department Default', compliance: 'SOX, PCI-DSS', rules_matched: '3' },
  },
  {
    id: 'intent',
    phase: 'routing',
    title: 'Intent Detection',
    subtitle: 'Intent Orchestration · NLP router',
    icon: Brain,
    duration: '124ms',
    status: 'pending',
    detail: 'Intent classified: financial_analysis / revenue_forecast_compare. Confidence 0.91. Routed to Finance Analysis orchestration.',
    metadata: { intent: 'financial_analysis', sub_intent: 'revenue_forecast_compare', confidence: '91%' },
  },
  {
    id: 'orchestration',
    phase: 'routing',
    title: 'Orchestration Match',
    subtitle: 'Agent Orchestrations · Finance Analysis',
    icon: GitBranch,
    duration: '18ms',
    status: 'pending',
    detail: 'Matched orchestration template fin-revenue-analysis-v3. Planner generated 5-step execution graph with 2 LLM calls.',
    metadata: { orchestration_id: 'orch-fin-rev-v3', steps_planned: '5', llm_calls: '2' },
  },
  {
    id: 'plan',
    phase: 'execution',
    title: 'Execution Planner',
    subtitle: 'Multi-step DAG compiled',
    icon: Layers,
    duration: '12ms',
    status: 'pending',
    detail: 'DAG: RAG → LLM#1 structure → MCP enrich → LLM#2 synthesize → validate. Parallel KB retrieval enabled.',
    metadata: { dag_nodes: '5', parallel_branches: '1' },
  },
  {
    id: 'rag',
    phase: 'execution',
    title: 'Knowledge Retrieval',
    subtitle: 'Knowledge Workspace · Finance KB',
    icon: Database,
    duration: '486ms',
    status: 'pending',
    detail: 'Vector search returned 5 chunks. 2 masked by policy. Reranked top-3 injected into LLM context.',
    tokens: 420,
    cost: '$0.008',
    metadata: { kb: 'Finance KB', chunks: '5', avg_similarity: '0.87' },
  },
  {
    id: 'llm1',
    phase: 'execution',
    title: 'LLM Call #1 — Structure',
    subtitle: 'GPT-4o-mini · Query planning',
    icon: Sparkles,
    duration: '412ms',
    status: 'pending',
    detail: 'First LLM pass structures analysis plan, extracts entities (Q3, revenue, forecast), defines MCP tool parameters.',
    isLlm: true,
    model: 'GPT-4o-mini',
    tokens: 840,
    cost: '$0.012',
    metadata: { temperature: '0.1', finish: 'stop', purpose: 'planning' },
  },
  {
    id: 'mcp',
    phase: 'execution',
    title: 'MCP Tool Call',
    subtitle: 'CustomerDB.Read · MCP Dashboard',
    icon: Wrench,
    duration: '186ms',
    status: 'pending',
    detail: 'Fetched customer segment data for revenue breakdown. Policy allowed read-only access to 3 records.',
    policyDecision: 'Allowed',
    metadata: { tool: 'CustomerDB.Read', records: '3', server: 'customer-db-prod' },
  },
  {
    id: 'llm2',
    phase: 'execution',
    title: 'LLM Call #2 — Synthesize',
    subtitle: 'GPT-4o · Final analysis',
    icon: Bot,
    duration: '1,482ms',
    status: 'pending',
    detail: 'Primary LLM synthesizes answer using RAG context + MCP data. Citations [1][2][3] generated. PII fields masked.',
    isLlm: true,
    model: 'GPT-4o',
    tokens: 3420,
    cost: '$0.068',
    metadata: { temperature: '0.2', finish: 'stop', citations: '3' },
  },
  {
    id: 'validate',
    phase: 'output',
    title: 'Output Validation',
    subtitle: 'Prompt Firewall · Output scan',
    icon: Shield,
    duration: '64ms',
    status: 'pending',
    detail: 'Output scanned for leakage, hallucination flags, compliance. 2 PII fields masked before delivery.',
    policyDecision: 'Mask PII · Deliver',
    metadata: { masked_fields: '2', safety_score: '96' },
  },
  {
    id: 'response',
    phase: 'output',
    title: 'Final Response',
    subtitle: 'Delivered to user',
    icon: Zap,
    duration: '8ms',
    status: 'pending',
    detail: 'Response streamed to client. Trace logged, audit retained, evaluation hooks queued.',
    metadata: { total_duration: '2.412s', total_cost: '$0.084', total_tokens: '4,680' },
  },
];

export function getFlowStepsForCall(stepIds: string[]): OrchestrationFlowStep[] {
  const set = new Set(stepIds);
  return ALL_FLOW_STEPS.filter((s) => set.has(s.id));
}
